import {Button, Hero} from '@primer/react-brand/esm'
import type {ReactNode, RefObject} from 'react'

import {
  renderContentfulDownloadActionMenu,
  useResolvedDownloadOptions,
} from '@github-ui/swp-core/components/contentful/ContentfulDownloadActionMenu'
import {usePersonalization} from '@github-ui/swp-core/contexts/PersonalizationContext'
import {getAnalyticsEvent} from '@github-ui/swp-core/lib/utils/analytics'
import {clsx} from 'clsx'

import {isDownloadOptions, isLink, toCoreDownloadOptions, type Action} from '../../types'
import {resolveCtaWithPersonalisation} from './resolve-hero-ctas'
import type {FlexSuiteBaseHeroProps} from './types'
import {getDescription, getHeading} from './utils'
import styles from './FlexSuiteHero.module.css'

type HeroContentProps = FlexSuiteBaseHeroProps & {
  'data-testid'?: string
  children?: ReactNode
  className?: string
  imageContainerClassName?: string
  imageContainerRef?: RefObject<HTMLDivElement | null>
  headingClassName?: string
}

function getHeroCtaAnalyticsEvent(
  action: string,
  position: 'primary' | 'secondary',
  audienceCondition: string | undefined,
) {
  return getAnalyticsEvent({
    action,
    tag: 'button',
    context: `${position} CTA ${audienceCondition ?? 'default'}`,
    location: 'hero',
  })
}

function renderHeroAction(
  action: Action | undefined,
  downloadOptions: ReturnType<typeof toCoreDownloadOptions> | undefined,
  resolvedDownloadOptions: ReturnType<typeof useResolvedDownloadOptions>,
  position: 'primary' | 'secondary',
  audienceCondition: string | undefined,
) {
  if (!action) return null

  if (downloadOptions && resolvedDownloadOptions) {
    return renderContentfulDownloadActionMenu({
      component: downloadOptions,
      resolved: resolvedDownloadOptions,
      analytics: {
        location: 'hero',
        context: `${position} CTA ${audienceCondition ?? 'default'}`,
      },
      variant: position,
      elementKey: position,
    })
  }

  if (!isLink(action) || !action.fields.href) return null

  return (
    <Button
      key={position}
      as="a"
      href={action.fields.href}
      variant={position}
      {...getHeroCtaAnalyticsEvent(action.fields.text, position, audienceCondition)}
    >
      {action.fields.text}
    </Button>
  )
}

export function HeroContent({
  heroHeading,
  heroLabel,
  heroDescription,
  heroCtaOne,
  heroCtaOnePersonalisation,
  heroCtaTwo,
  heroCtaTwoPersonalisation,
  heroTertiaryDescription,
  options,
  children,
  className,
  'data-testid': dataTestId,
  imageContainerClassName,
  imageContainerRef,
  headingClassName,
}: HeroContentProps) {
  const {userState} = usePersonalization()
  const {variant, enableAnimation, align, textVariant} = options
  const trailingComponent = heroTertiaryDescription
    ? () => getDescription(heroTertiaryDescription, textVariant)
    : undefined
  const {cta: resolvedHeroCtaOne, audienceCondition: heroCtaOneAudienceCondition} = resolveCtaWithPersonalisation(
    heroCtaOne,
    heroCtaOnePersonalisation,
    userState,
  )
  const {cta: resolvedHeroCtaTwo, audienceCondition: heroCtaTwoAudienceCondition} = resolveCtaWithPersonalisation(
    heroCtaTwo,
    heroCtaTwoPersonalisation,
    userState,
  )
  const hasPersonalizedCta = heroCtaOneAudienceCondition !== undefined || heroCtaTwoAudienceCondition !== undefined
  const heroCtaOneDownloadOptions = isDownloadOptions(resolvedHeroCtaOne)
    ? toCoreDownloadOptions(resolvedHeroCtaOne)
    : undefined
  const heroCtaTwoDownloadOptions = isDownloadOptions(resolvedHeroCtaTwo)
    ? toCoreDownloadOptions(resolvedHeroCtaTwo)
    : undefined
  const resolvedHeroCtaOneDownloadOptions = useResolvedDownloadOptions(heroCtaOneDownloadOptions)
  const resolvedHeroCtaTwoDownloadOptions = useResolvedDownloadOptions(heroCtaTwoDownloadOptions)
  const primaryAction = renderHeroAction(
    resolvedHeroCtaOne,
    heroCtaOneDownloadOptions,
    resolvedHeroCtaOneDownloadOptions,
    'primary',
    heroCtaOneAudienceCondition,
  )
  const secondaryAction = renderHeroAction(
    resolvedHeroCtaTwo,
    heroCtaTwoDownloadOptions,
    resolvedHeroCtaTwoDownloadOptions,
    'secondary',
    heroCtaTwoAudienceCondition,
  )
  const actions = !primaryAction ? secondaryAction : secondaryAction ? [primaryAction, secondaryAction] : primaryAction
  const hasDownloadAction = Boolean(heroCtaOneDownloadOptions || heroCtaTwoDownloadOptions)

  return (
    <>
      <meta name="octolytics-dimension-personalized" content={hasPersonalizedCta ? 'yes' : 'no'} />
      <Hero
        className={clsx(className, hasDownloadAction && styles.heroWithActionMenu)}
        variant={variant}
        align={align}
        enableAnimation={enableAnimation}
        trailingComponent={trailingComponent}
        data-testid={dataTestId}
        imageContainerClassName={imageContainerClassName}
        imageContainerRef={imageContainerRef}
      >
        {heroLabel && <Hero.Label>{heroLabel}</Hero.Label>}
        <Hero.Heading className={headingClassName}>{getHeading(heroHeading)}</Hero.Heading>
        {heroDescription ? (
          <Hero.Description variant={textVariant}>{getDescription(heroDescription, textVariant)}</Hero.Description>
        ) : null}
        <Hero.ButtonGroup className={styles.heroActions}>{actions ?? []}</Hero.ButtonGroup>
        {children}
      </Hero>
    </>
  )
}
